import './SwapPagesModal.pcss';
import React, { useState } from 'react';
import moment from 'moment/moment';
import { without } from 'lodash';
import { compose } from 'recompose';
import { connect, useSelector, useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import subscriptionCRUDActions from '@mm/state/actions/SubscriptionCRUDActions';
import ConnectedPageModel from '@mm/state/model/ConnectedPageModel';
import ArrowIcon, { ARROW_TYPES } from '@mm/icons/general/ArrowIcon';
import { SubscriptionModel } from '@mm/state/model/SubscriptionModel';
import modalActions, {
  SwapPagesModalParams,
} from '@mm/state/modules/modalsState/actions';
import { StoreState } from '@mm/state/store-interfaces';
import { OnClickElsewhere } from '../../../../../common/components/OnClickElsewhere/OnClickElsewhere';
import { useSafeState } from '../../../../../common/hooks/use-safe-state';
import { ModalInstanceProps } from '../../../../../common/modals/interfaces';
import BasicModalTemplate from '../../../../../common/modals/BasicModalTemplate';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';

type OuterProps = SwapPagesModalParams & ModalInstanceProps;

type InnerProps = ReturnType<typeof mapDispatchToProps>;

type Props = OuterProps & InnerProps;

const SwapPagesModal = (props: Props) => {
  const {
    freePages,
    subscription,
    closeModal,
    facebookPage,
    openConfirmSwapPagesModal,
    swapPages,
  } = props;
  const dispatch = useDispatch();
  const [selectedPage, setSelectedPage] = useState(freePages[0]);
  const [makingRequest, setMakingRequest] = useSafeState(false);
  const [showingDropdown, setShowingDropdown] = useState(false);
  const twilioNumber = useSelector(
    (state: StoreState) => state.twilio.numbers,
  )[0];

  const performRequest = async () => {
    setMakingRequest(true);
    const response = await swapPages(
      subscription,
      facebookPage.id,
      selectedPage.id,
    );
    setMakingRequest(false);
    if (response && response.success) {
      closeModal();
      openConfirmSwapPagesModal(selectedPage);
    }
  };

  const onConfirm = async () => {
    if (twilioNumber) {
      dispatch(
        modalActions.openGeneralConfirmModal({
          headerText: 'Your phone number will be lost!',
          messageText: `Your Facebook Page has an active phone number associated. 
            If you transfer your subscription, this phone number and all associated SMS contacts will be permanently lost.`,
          cancelButtonFirst: false,
          okButtonText: 'Transfer anyway',
          cancelButtonText: 'Never mind',
          onCloseHandler: async (ok: boolean) => {
            if (ok) {
              performRequest();
            }
          },
        }),
      );
      return;
    }
    performRequest();
  };

  const renderItem = (
    facebookPage: ConnectedPageModel,
    index?: number,
    withHandler?: boolean,
  ) => (
    <div
      className="swap-pages-modal__page-select-item"
      onClick={() => {
        if (withHandler) {
          setSelectedPage(facebookPage);
          setShowingDropdown(false);
        }
      }}
      key={index}
    >
      <img
        src={facebookPage.squarePicture}
        className="swap-pages-modal__page-select-item-image"
        alt="Facebook page icon"
      />
      <div className="swap-pages-modal__page-select-item-meta-wrapper">
        <div className="swap-pages-modal__page-select-item-meta-top">
          <div className="swap-pages-modal__page-select-item-name-wrapper">
            {facebookPage.name}
          </div>
        </div>
        <div className="swap-pages-modal__page-select-item-connected-since">
          Connected Since: {moment(facebookPage.createdAt).format('MM/DD/YYYY')}
        </div>
      </div>
    </div>
  );

  const selectableFreePages = without(freePages, selectedPage);

  return (
    <BasicModalTemplate
      className="swap-pages-modal"
      withMonkeyBackground={false}
      iconClass="swap-pages-modal__close-icon"
    >
      <div className="swap-pages-modal__body">
        <div className="swap-pages-modal__header-text">
          Upgraded the wrong page?
        </div>
        <div className="swap-pages-modal__subheader-text">
          We can help! Just tell us which page you’d like to transfer your
          subscription to.
        </div>
        <div className="swap-pages-modal__pages">
          <OnClickElsewhere
            handleClick={() => setShowingDropdown(false)}
            className="swap-pages-modal__dropdown-wrapper"
          >
            <div
              className="swap-pages-modal__dropdown-opener"
              onClick={() =>
                selectableFreePages.length > 0 &&
                setShowingDropdown(!showingDropdown)
              }
            >
              {renderItem(selectedPage)}
              {selectableFreePages.length > 0 && (
                <div className="swap-pages-modal__arrow-wrapper">
                  <ArrowIcon
                    direction={showingDropdown ? 'top' : 'bottom'}
                    color="#525252"
                    size={20}
                    arrowType={ARROW_TYPES.CHEVRON}
                  />
                </div>
              )}
            </div>
            {showingDropdown && (
              <div className="swap-pages-modal__dropdown-menu-items-container">
                <div className="swap-pages-modal__dropdown-items-container">
                  {selectableFreePages.map((page, index) =>
                    renderItem(page, index, true),
                  )}
                </div>
              </div>
            )}
          </OnClickElsewhere>
        </div>
        <div className="swap-pages-modal__buttons">
          <div
            onClick={() => closeModal()}
            className="swap-pages-modal__cancel-button"
          >
            Cancel
          </div>
          <PrimaryButton
            disabled={makingRequest}
            onClick={onConfirm}
            className="swap-pages-modal__confirm-button"
          >
            Transfer Subscription
          </PrimaryButton>
        </div>
      </div>
    </BasicModalTemplate>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  openConfirmSwapPagesModal: (facebookPage: ConnectedPageModel) =>
    dispatch(modalActions.openConfirmSwapPagesModal({ facebookPage })),
  swapPages: (
    subscription: SubscriptionModel,
    pageId: number,
    newPageId: number,
  ) =>
    dispatch(
      subscriptionCRUDActions.swapPages(subscription, pageId, newPageId),
    ),
});

const enhance = compose<InnerProps, OuterProps>(
  connect(null, mapDispatchToProps),
);

export default enhance(SwapPagesModal);
